import { useCallback, useEffect, useState } from "react";
import useAuth from "../useAuth";
import useWebotServer from "../useWebotServer";
import useOrgDirectory from "../useOrgDirectory";
import { crmHandlerView } from "../../components/_dashboard/crm/handler/crmHandlerFace";
import {
  claimUnknownContactIds,
  readContactHandlers,
  recordContactHandlers,
  releaseContactIds,
  setContactHandlerLocally,
  subscribeToContactHandlers
} from "./contactHandlerStore";
import type { CrmHandlerView } from "../../components/_dashboard/crm/handler/crmHandlerFace";
import type { REST_schema } from "../../@types/sharedSchema";

/**
 * WHO THE AGENCY DESIGNATED TO HANDLE THE PEOPLE ON THIS PAGE — the מתווך מטפל badge.
 *
 * One batched request per loaded page, issued AFTER the list and never blocking it, in the
 * same shape as `useCrmHolders`. Two things differ, and both are the point of the feature.
 *
 * ── ⚠️ THE GATE IS *NOT* `canBrowseOrg && canSeePool` ──────────────────────────
 *
 * That is what `useCrmHolders` uses, and the server's `DEFAULT_PERMISSIONS.member` is
 * `{ pool: 'basic', browse: false }` — so copying it here would mean the badge is fetched for
 * org admins and for nobody else, i.e. for everyone except the ordinary realtors who would
 * otherwise cold-call a lead a colleague is already running. The endpoint behind this is a
 * separate one for exactly that reason and is authorized on plain org membership.
 *
 * Gated on the DIRECTORY rather than on `useCrmMyAccess` so the first frame after a reload
 * can decide: the directory is persisted to localStorage and answers synchronously, while
 * `useCrmMyAccess` is the SECOND of two sequential round trips. It also keeps the request
 * from ever being issued for the ~95% of the fleet in no agency at all.
 *
 * ── THE CACHE IS A MODULE STORE, SHARED WITH `useContactHandler` ───────────────
 *
 * See `contactHandlerStore.ts`. A designation has no redaction tier, so unlike a holders
 * answer it is cacheable across queries — which is what lets the list, the panel, the
 * new-contact modal and the pickers read one answer instead of four.
 *
 * ── FAILURE IS SILENCE, AND `undefined` NEVER BECOMES `null` ───────────────────
 *
 * A failed lookup renders no badge, exactly like a caller with no agency. It must NOT cache
 * "nobody is designated": that is an affirmative claim, and printing it over a contact a
 * colleague is actually handling is the one wrong answer this feature can give.
 */
export type CrmAssignmentsState = {
  /** The agency answered at all. False for a solo realtor and for a failed request. */
  enabled: boolean;
  /** `undefined` = not answered / failed. `null` = answered, nobody designated. */
  handlerFor: (contactId: string) => CrmHandlerView | null | undefined;
  /** Write-through for the assign dialog, so the badge repaints before any refetch. */
  setLocal: (contactId: string, assignment: REST_schema.CrmContactAssignment | null) => void;
};

/**
 * Picks the designation to RENDER when a caller belongs to several agencies.
 *
 * The list is scoped to one org whenever `orgId` is set, so this only bites the non-CRM
 * surfaces (the new-contact modal, the pickers) where there is no org context at all. The
 * rule is "prefer a live designation over a stale one, then the first the server returned" —
 * a stale entry names someone who has left, and showing that over a current handler would be
 * actively misleading.
 */
const pickAssignment = (entries: REST_schema.CrmContactAssignment[] | null | undefined): REST_schema.CrmContactAssignment | null => {
  if (!entries || entries.length === 0) return null;
  return entries.find((entry) => !entry.stale) ?? entries[0];
};

export default function useCrmAssignments({
  contactIds,
  orgId,
  enabled: enabledOverride
}: {
  /** The loaded page's contact ids. Stable string identity is not required. */
  contactIds: readonly string[];
  orgId?: string;
  /** Defaults to "the caller is in at least one agency". */
  enabled?: boolean;
}): CrmAssignmentsState {
  const { contactsAssignments } = useWebotServer();
  const { getMember, orgs } = useOrgDirectory();
  const { user } = useAuth();
  const viewerUserId = (user?.email as string | undefined) ?? null;

  const enabled = enabledOverride ?? orgs.length > 0;
  const [answered, setAnswered] = useState(false);

  // Re-renders when ANY surface on the page records an answer — including one this hook did
  // not ask for, which is the whole point of a shared store: the panel repaints when the
  // list's request lands, without either of them knowing about the other.
  //
  // A subscriber set plus a version counter, matching `useOrgDirectory`. Not
  // `useSyncExternalStore`: this app is on React 17, which does not have it.
  const [, setVersion] = useState(0);
  useEffect(() => subscribeToContactHandlers(() => setVersion((value) => value + 1)), []);

  // `ids` stands for the array: it changes exactly when the loaded set does, and unlike the
  // array itself it is stable across the re-renders an inline edit causes.
  const ids = contactIds.join(",");

  useEffect(() => {
    if (!enabled) return;
    const wanted = [...new Set(contactIds)].filter(Boolean);
    // Claims and marks in flight in one pass, so two surfaces rendering the same phone at
    // the same moment issue ONE request rather than two.
    const missing = claimUnknownContactIds(orgId, wanted);
    if (missing.length === 0) {
      setAnswered(true);
      return;
    }

    let cancelled = false;
    contactsAssignments({ orgId, contactIds: missing })
      .then((response) => {
        if (cancelled) {
          releaseContactIds(orgId, missing);
          return;
        }
        recordContactHandlers(orgId, missing, response.assignments ?? {});
        setAnswered(response.enabled);
      })
      .catch((error) => {
        // Silent by design — see the header. The ids are RELEASED, not cached as "nobody",
        // so the next page load retries and nothing renders an affirmative empty seat.
        releaseContactIds(orgId, missing);
        // eslint-disable-next-line no-console
        console.error("useCrmAssignments: failed to load contact assignments", error);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsAssignments, enabled, orgId, ids]);

  const handlerFor = useCallback(
    (contactId: string): CrmHandlerView | null | undefined => {
      if (!enabled) return undefined;
      const entry = readContactHandlers(orgId, contactId);
      if (entry === undefined) return undefined;
      const picked = pickAssignment(entry);
      if (!picked) return null;
      // The FACE comes from the directory, which is the only source for an assignee who
      // holds no copy of this contact — see `crmHandlerFace`.
      return crmHandlerView(picked, getMember(picked.assigneeUserId), viewerUserId);
    },
    [enabled, orgId, getMember, viewerUserId]
  );

  const setLocal = useCallback(
    (contactId: string, assignment: REST_schema.CrmContactAssignment | null) => setContactHandlerLocally(orgId, contactId, assignment),
    [orgId]
  );

  return { enabled: enabled && answered, handlerFor, setLocal };
}
